import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ThermometerSun, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { SensorCard } from '../components/Sensors/SensorCard';
import { SensorChart } from '../components/Sensors/SensorChart';

const REFRESH_MS = 20000;
const PERIODS = ['1h', '24h', '7d', '30d'];
const PALETTE = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#a855f7', '#14b8a6', '#ec4899', '#64748b'];

const Segmented = ({ options, value, onSelect }) => (
    <div className="inline-flex gap-1 bg-muted/40 rounded-lg p-1">
        {options.map(o => (
            <button key={o.value} type="button" onClick={() => onSelect(o.value)} aria-pressed={value === o.value}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${value === o.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
                {o.label}
            </button>
        ))}
    </div>
);

export function Sensors() {
    const { t } = useTranslation();
    const { token } = useAuth();
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    const [sensors, setSensors] = useState(null);
    const [hidden, setHidden] = useState(() => new Set());
    const [period, setPeriod] = useState('24h');
    const [metric, setMetric] = useState('temperature');
    const [history, setHistory] = useState(() => ({ series: {}, until: Date.now() }));
    const [failed, setFailed] = useState(false);

    const api = useCallback(async (path) => {
        const res = await fetch(`/api/sensors${path}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
    }, []);

    const colors = useMemo(
        () => Object.fromEntries((sensors ?? []).map((s, i) => [s.id, PALETTE[i % PALETTE.length]])),
        [sensors]
    );
    const visible = useMemo(() => (sensors ?? []).filter(s => !hidden.has(s.id)), [sensors, hidden]);
    const visibleIds = visible.map(s => s.id).join(',');

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const d = await api('');
                if (alive) { setSensors(d); setFailed(false); }
            } catch { if (alive) setFailed(true); }
        };
        const tick = () => { if (document.visibilityState === 'visible') load(); };
        const first = setTimeout(load, 0);
        const id = setInterval(tick, REFRESH_MS);
        document.addEventListener('visibilitychange', tick);   // refresh at once when coming back to the tab
        return () => { alive = false; clearTimeout(first); clearInterval(id); document.removeEventListener('visibilitychange', tick); };
    }, [api]);

    useEffect(() => {
        if (!visibleIds) return undefined;
        let alive = true;
        const load = async () => {
            try {
                const d = await api(`/history?ids=${visibleIds}&period=${period}`);
                if (alive) setHistory({ series: d.series, until: Date.now() });
            } catch { /* keep the previous curves */ }
        };
        const tick = () => { if (document.visibilityState === 'visible') load(); };
        const first = setTimeout(load, 0);
        const id = setInterval(tick, REFRESH_MS);
        document.addEventListener('visibilitychange', tick);
        return () => { alive = false; clearTimeout(first); clearInterval(id); document.removeEventListener('visibilitychange', tick); };
    }, [api, visibleIds, period]);

    const toggle = (id) => setHidden(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    if (!sensors && !failed) return (
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />{t('sensors.loading')}
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto w-full pb-8">
            <div className="mb-6">
                <h2 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                    <ThermometerSun className="w-7 h-7 text-primary" />{t('nav.sensors')}
                </h2>
                <p className="text-muted-foreground mt-2">{t('sensors.subtitle')}</p>
            </div>

            {failed && !sensors && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
                    {t('sensors.loadFailed')}
                </div>
            )}

            {sensors && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                        {sensors.map(s => (
                            <SensorCard key={s.id} sensor={s} color={colors[s.id]} selected={!hidden.has(s.id)} onToggle={() => toggle(s.id)} />
                        ))}
                    </div>

                    <section className="bg-card border border-border rounded-xl p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <Segmented value={metric} onSelect={setMetric}
                                options={[{ value: 'temperature', label: t('sensors.temperature') }, { value: 'humidity', label: t('sensors.humidity') }]} />
                            <Segmented value={period} onSelect={setPeriod}
                                options={PERIODS.map(p => ({ value: p, label: t(`sensors.period.${p}`) }))} />
                        </div>
                        {visible.length === 0
                            ? <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">{t('sensors.noneSelected')}</div>
                            : <SensorChart series={history.series} sensors={visible} colors={colors} metric={metric} period={period} until={history.until} />}
                    </section>
                </>
            )}
        </div>
    );
}
